import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OrderProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/orders_service";
import { useSelector } from "react-redux";
import { CoreReduxState } from "../store";
import { MED_SKU } from "src/utils/medEligibility";
import { BillingUserProto } from "@noom/noom-contracts/noom_contracts/billing/user/billing_user";
import { BillingCyclePreviewProto } from "@noom/noom-contracts/noom_contracts/billing/bms/subscriptions/billing_cycle_preview";
import { CustomerDetailsProto } from "@noom/noom-contracts/noom_contracts/billing/bms/common/shared";
import { OrderOperationProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/order_operation";
import { MoneyProto } from "@noom/noom-contracts/noom_contracts/lib/money";
import { InvoiceProto } from "@noom/noom-contracts/noom_contracts/billing/bms/invoices/invoice";
import { PurchaseTypeEnumProto_Value } from "@noom/noom-contracts/noom_contracts/billing/shared";

export type SaleItemInfo = {
  medSku?: MED_SKU;
  saleItemId?: string;
  trialSaleItemId?: string;
  planName?: string;
  legacyNoomPlanId?: string;
  pspPlanId?: string;
};

export type DiscountInfo = {
  promoCode?: string;
  percentage?: number;
  fixedAmount?: MoneyProto;
};

export type CheckoutOrderEntry = {
  productName: string;
  purchaseType?: PurchaseTypeEnumProto_Value;
  saleItem?: SaleItemInfo;
  operation?: OrderOperationProto;
  order?: OrderProto;
  initialBillingCycle?: BillingCyclePreviewProto;
  recurringBillingCycle?: BillingCyclePreviewProto;
  invoice?: InvoiceProto;
  discount?: DiscountInfo;
};

export type PrefetchedEltv = {
  eltvValue: number;
};

export type CheckoutOrdersState = {
  saleItem?: SaleItemInfo;
  customerDetails?: CustomerDetailsProto;
  operation?: OrderOperationProto;
  billingUser?: BillingUserProto;
  order?: OrderProto;
  initialBillingCycle?: BillingCyclePreviewProto;
  recurringBillingCycle?: BillingCyclePreviewProto;
  refundAmount?: MoneyProto;
  hasInitializationFailed?: boolean;
  addons?: Record<string, CheckoutOrderEntry>;
  addonsCart?: string[];
  prefetchedEltv?: PrefetchedEltv | null;
};

const checkoutOrdersStateSlice = createSlice({
  name: "checkoutOrdersState",
  initialState: {} as CheckoutOrdersState,
  reducers: {
    setCheckoutOrders: (state, action: PayloadAction<CheckoutOrdersState>) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    setCheckoutSaleItem: (state, action: PayloadAction<SaleItemInfo>) => {
      return {
        ...state,
        saleItem: action.payload,
        billingAddress: undefined,
        order: undefined,
      };
    },
    setCheckoutCustomerDetails: (
      state,
      action: PayloadAction<CustomerDetailsProto>
    ) => {
      return {
        ...state,
        customerDetails: action.payload,
      };
    },
    setCheckoutOrderOperation: (
      state,
      action: PayloadAction<OrderOperationProto>
    ) => {
      return {
        ...state,
        operation: action.payload,
      };
    },
    setBillingUser: (state, action: PayloadAction<BillingUserProto>) => {
      return {
        ...state,
        billingUser: action.payload,
      };
    },
    setHasInitializationFailed: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        hasInitializationFailed: action.payload,
      };
    },
    setAddonEntries: (
      state,
      action: PayloadAction<Record<string, CheckoutOrderEntry>>
    ) => {
      return {
        ...state,
        addons: action.payload,
      };
    },
    setAddonsCartEntries: (state, action: PayloadAction<string[]>) => {
      return {
        ...state,
        addonsCart: action.payload,
      };
    },
    clearAddonsCart: (state) => {
      return {
        ...state,
        addonsCart: undefined,
      };
    },
    setPrefetchedEltv: (
      state,
      action: PayloadAction<PrefetchedEltv | null | undefined>
    ) => {
      return {
        ...state,
        prefetchedEltv: action.payload,
      };
    },
  },
});

export const {
  setCheckoutOrders,
  setCheckoutSaleItem,
  setCheckoutCustomerDetails,
  setCheckoutOrderOperation,
  setBillingUser,
  setHasInitializationFailed,
  setAddonEntries,
  setAddonsCartEntries,
  clearAddonsCart,
  setPrefetchedEltv,
} = checkoutOrdersStateSlice.actions;
export default checkoutOrdersStateSlice;

export function useCheckoutOrders() {
  return useSelector((state: CoreReduxState) => state.checkoutOrders);
}

const selectAddonsCart = createSelector(
  (state: CoreReduxState) => state.checkoutOrders.addonsCart,
  (state: CoreReduxState) => state.checkoutOrders.addons,
  (addonsCart, addons) => {
    if (!addonsCart || !addons) return undefined;
    return addonsCart.map((key) => addons[key]).filter(Boolean);
  }
);

export function useAddonsCart(): CheckoutOrderEntry[] | undefined {
  return useSelector(selectAddonsCart);
}
